import './FeedItems.sass';
import { ContentPriorityPlaceholder } from '$core/Feed/ContentPriorityPlaceholder/ContentPriorityPlaceholder';
import {
  FeaturedProfile,
  Loader,
  Post,
  PromoPost,
  PromoProfile,
  Slider,
} from '$shared/components';
import { List, ListItem, Placeholder } from '$uikit';
import { OnboardingPlaceholder } from '$core/Feed/OnboardingPlaceholder/OnboardingPlaceholder';
import { FeedItemType } from '$store/feed/interface';
import { SuggestionPlaceholder } from '$core/Feed/SuggestionsPlaceholder/SuggestionsPlaceholder';
import { EmptyPlaceholder } from '$core/Feed/EmptyPlaceholder/EmptyPlaceholder';
import React, { useMemo } from 'react';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { FeedItemsProps } from '$core/Feed/FeedItems/FeedItems.interface';
import { RecommendationsSliderItem } from '$core/Feed/RecommendationsSliderItem/RecommendationsSliderItem';
import { isTesterId } from '$utils';
import { FeedModel } from '$core/Feed/FeedModel/FeedModel';
import { BundleModel, FeedModelRow } from '$store/models';
import { FeedOnlineModel } from '$core/Feed/FeedOnlineModel/FeedOnlineModel';
import { FeedBundle } from '$core/Feed/FeedBundle/FeedBundle';
import { goTo } from '$navigation/helper';
import { FEED_PROMOTION } from '$shared/constants/pages';

const FeedItemsComponent = ({
  isSuggestions,
  activeTab,
  isContentPriority,
  isLoading,
  feed,
  isAdult,
  isMoreItems,
  sources,
}: FeedItemsProps) => {
  const t = useTranslator();
  const currentUser = useCurrentUserInfo();

  const canSeeSource = useMemo(() => {
    if (!currentUser) {
      return false;
    }

    return isTesterId(currentUser.id);
  }, [currentUser]);

  if (
    (isSuggestions || activeTab === 'following' || activeTab === 'smart') &&
    !isContentPriority
  ) {
    return <ContentPriorityPlaceholder centered />;
  }

  if (!feed?.length) {
    if (isLoading) {
      return (
        <Placeholder centered>
          <Loader isWrapped />
        </Placeholder>
      );
    }

    if (activeTab === 'liked') {
      return (
        <Placeholder centered title={t('feed_likes_empty_title')}>
          {t('feed_likes_empty_caption')}
        </Placeholder>
      );
    }

    if (activeTab === 'sponsored') {
      return (
        <Placeholder centered title={t('feed_sponsored_empty_title')}>
          {t('feed_sponsored_empty_caption')}
        </Placeholder>
      );
    }

    return <OnboardingPlaceholder centered />;
  }

  return (
    <div className="Feed__items">
      {currentUser?.verified === 2 && (
        <List>
          <ListItem chevron onClick={() => goTo(FEED_PROMOTION)}>
            {t('feed_promotion_title')}
          </ListItem>
        </List>
      )}
      {feed.map((item, i) => {
        let cont;
        if (item.type === FeedItemType.BUNDLE) {
          return (
            <FeedBundle
              key={item.id}
              bundle={item.object as BundleModel}
              showMoreButton={activeTab !== 'bundles' && false}
            />
          );
        } else if (item.type === FeedItemType.MODEL) {
          return <FeedModel key={item.id} object={item.object as FeedModelRow} />;
        } else if (item.type === FeedItemType.ONLINE_MODEL) {
          const data = item.object as any;
          return <FeedOnlineModel key={`online_model_${data.userId}`} object={data} />;
        } else if (item.type === FeedItemType.AD_PROMO_POST) {
          return (
            <PromoPost key={`ad_${item.id}`} post={item.object as any} adId={item.id} />
          );
        } else if (item.type === FeedItemType.AD_PROMO_PROFILE) {
          const data = item.object as any;
          return (
            <PromoProfile
              key={`ad_${item.id}`}
              cover={data.cover}
              avatar={data.avatar}
              userId={data.ownerId}
              adId={item.id}
            />
          );
        } else if (item.type === FeedItemType.FEATURED_PROFILE) {
          const data = item.object as any;
          return (
            <FeaturedProfile
              key={`featured_${item.id}`}
              cover={data.coverUrl}
              userId={data.userId}
            />
          );
        } else if (item.type === FeedItemType.POST || item.type === FeedItemType.ADS) {
          const postId = item.object as number;
          cont = (
            <Post
              sourceLabel={(canSeeSource && sources && sources[postId]) || undefined}
              key={item.id}
              postId={postId}
              showFollowBtn={activeTab !== 'following' && activeTab !== 'liked'}
              isRecommendations={activeTab === 'smart'}
              isAds={item.type === FeedItemType.ADS}
              isFeed
            />
          );
        } else if (
          item.type === FeedItemType.RECOMMENDATION &&
          isAdult &&
          activeTab === 'smart'
        ) {
          return (
            <Slider
              key={item.id}
              headerText={t('recommended_models_header')}
              className="RecommendationsSlider"
            >
              {(item.object as number[]).map((item) => (
                <RecommendationsSliderItem userId={item} key={item} />
              ))}
            </Slider>
          );
        } else {
          cont = null;
        }

        return (
          <div key={item.id}>
            {!isSuggestions && i > 0 && item.isSuggestion && !feed[i - 1].isSuggestion && (
              <>
                <SuggestionPlaceholder />
                {!isContentPriority && (
                  <>
                    <div className="Feed__separator" />
                    <ContentPriorityPlaceholder />
                  </>
                )}
              </>
            )}
            {!isSuggestions && i === 0 && item.isSuggestion && (
              <>
                <OnboardingPlaceholder />
                <div className="Feed__separator" />
                <SuggestionPlaceholder />
              </>
            )}
            {!item.isSuggestion || isContentPriority ? cont : <></>}
          </div>
        );
      })}
      {activeTab !== 'liked' && activeTab !== 'sponsored' && !isMoreItems && (
        <EmptyPlaceholder />
      )}
    </div>
  );
};

export const FeedItems = React.memo(FeedItemsComponent);
